/**
 * useDistanceFromCenter Hook
 * Tracks an element's distance from the center of a scroll container
 * Returns a Framer Motion value (0 = centered, 1 = at max distance)
 */

import { useEffect, useLayoutEffect, RefObject } from 'react';
import { useMotionValue, MotionValue } from 'framer-motion';

const SCROLLABLE_OVERFLOW_AUTO = 'auto';
const SCROLLABLE_OVERFLOW_SCROLL = 'scroll';

interface UseDistanceFromCenterOptions {
  /** Ref to the element being tracked */
  elementRef: RefObject<HTMLElement | null>;
  /** Ref to the scroll container (defaults to element's scroll parent) */
  containerRef?: RefObject<HTMLElement | null>;
  /** Maximum distance for normalization (default: half container width) */
  maxDistance?: number;
  /** Initial raw distance used before the first DOM measurement. */
  initialRawDistance?: number;
  /** Whether to actively track distance (default: true) */
  enabled?: boolean;
}

interface UseDistanceFromCenterReturn {
  /** Distance from center as 0-1 (0 = centered, 1 = at max distance) */
  distance: MotionValue<number>;
  /** Raw pixel distance from center */
  rawDistance: MotionValue<number>;
}

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface CalculateInitialDistanceInput {
  maxDistance?: number;
  rawDistance: number;
}

function calculateInitialDistance({
  maxDistance,
  rawDistance,
}: CalculateInitialDistanceInput): number {
  if (rawDistance <= 0) {
    return 0;
  }

  if (maxDistance && maxDistance > 0) {
    return Math.min(rawDistance / maxDistance, 1);
  }

  return 1;
}

export function useDistanceFromCenter({
  elementRef,
  containerRef,
  maxDistance: customMaxDistance,
  initialRawDistance = 0,
  enabled = true,
}: UseDistanceFromCenterOptions): UseDistanceFromCenterReturn {
  const normalizedInitialRawDistance = Math.max(0, initialRawDistance);
  const distance = useMotionValue(calculateInitialDistance({
    maxDistance: customMaxDistance,
    rawDistance: normalizedInitialRawDistance,
  }));
  const rawDistance = useMotionValue(normalizedInitialRawDistance);

  useIsomorphicLayoutEffect(() => {
    if (!enabled) {
      distance.set(1);
      rawDistance.set(0);
      return;
    }

    const element = elementRef.current;
    if (!element) return;

    // Find scroll container - use provided ref or find scroll parent
    const container = containerRef?.current || findScrollParent(element);
    if (!container) return;

    let rafId: number | null = null;

    const calculateDistance = () => {
      // Normalize getBoundingClientRect values by body zoom to get CSS pixel distances.
      // getBoundingClientRect returns visual (post-zoom) pixels; dividing by zoom
      // converts to CSS pixel space so thresholds and distances are zoom-independent.
      const zoom = parseFloat(getComputedStyle(document.body).zoom || '1') || 1;

      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      // Calculate centers in CSS pixel space
      const containerCenter = (containerRect.left + containerRect.width / 2) / zoom;
      const elementCenter = (elementRect.left + elementRect.width / 2) / zoom;

      // Calculate raw distance in CSS pixels
      const rawDist = Math.abs(elementCenter - containerCenter);
      rawDistance.set(rawDist);

      // Normalize distance (0 = centered, 1 = at max distance)
      const maxDist = customMaxDistance ?? containerRect.width / (2 * zoom);
      const normalizedDist = Math.min(rawDist / maxDist, 1);
      distance.set(normalizedDist);
    };

    const handleScroll = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(calculateDistance);
    };

    // Defer initial calculation to avoid blocking LCP paint
    rafId = requestAnimationFrame(calculateDistance);

    // Listen to scroll
    container.addEventListener('scroll', handleScroll, { passive: true });

    // Also listen to window resize
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [elementRef, containerRef, customMaxDistance, enabled]);

  return { distance, rawDistance };
}

/**
 * Find the nearest scrollable parent element
 */
function findScrollParent(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;

  while (parent) {
    const style = getComputedStyle(parent);
    const overflowX = style.overflowX;

    if (overflowX === SCROLLABLE_OVERFLOW_AUTO || overflowX === SCROLLABLE_OVERFLOW_SCROLL) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
}
